import { useAuth } from "@/context/auth/AuthContext";
import { SupportedLocale } from "@/i18n";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import useMediaAttachment from "@/hooks/useMediaAttachment";
import { generateImageName } from "@/lib/utils";
import { CroppedImageInput } from "../form-fields/CroppedImageInput";
import * as AF from "../form-items/AbstractForm";

export interface ArtistFormProps {
  language: SupportedLocale;
}

export const NewArtistForm = ({ language }: ArtistFormProps) => {
  const { t } = useTranslation();
  const { instanceData } = useAuth();

  const imagePosition = useMemo(
    () => `artist${language.toLocaleUpperCase()}`,
    [language],
  );

  const { attachment, previewImageUrl, refetch } = useMediaAttachment(
    imagePosition,
    instanceData?.id,
  );

  return (
    <AF.Col>
      <AF.Text
        name={`${language}.pageName`}
        label={t("cms.artist.form.pageName")}
      />

      <CroppedImageInput
        docId={instanceData?.id}
        imageURL={previewImageUrl}
        attachment={attachment}
        refetch={refetch}
        imagePosition={imagePosition}
        aspect={1.27}
        label={t("cms.artist.form.portraitArtistLabel")}
        buildImageName={(instanceName) =>
          generateImageName({
            primaryType: "CMSPages",
            instanceName,
            translatedPageName: t("seo.imageName.artist"),
          })
        }
      />

      <AF.Text
        name={`${language}.credits`}
        label={t("cms.artist.form.credits")}
        tootltip={t("cms.artist.form.credits.tooltip")}
      />

      <AF.RichText
        name={`${language}.resumeArtist`}
        label={t("cms.artist.form.resumeArtist")}
      />
    </AF.Col>
  );
};

export const BiographyForm = ({ language }: ArtistFormProps) => {
  const { t } = useTranslation();

  return (
    <AF.Col>
      <AF.Text
        name={`${language}.biographySectionTitle`}
        label={t("cms.artist.form.biographySectionTitle")}
      />
      <AF.RichText
        name={`${language}.biographyContent`}
        label={t("cms.artist.form.biographyContent")}
      />
    </AF.Col>
  );
};

export const CollectionsForm = ({ language }: ArtistFormProps) => {
  const { t } = useTranslation();

  return (
    <AF.Col>
      <AF.Text
        name={`${language}.collectionsSectionTitle`}
        label={t("cms.artist.form.collectionsSectionTitle")}
      />
      <AF.RichText
        name={`${language}.collectionsContent`}
        label={t("cms.artist.form.collectionsContent")}
      />
    </AF.Col>
  );
};

export const ExhibitionsForm = ({ language }: ArtistFormProps) => {
  const { t } = useTranslation();

  return (
    <AF.Col>
      <AF.Text
        name={`${language}.exhibitionsSectionTitle`}
        label={t("cms.artist.form.exhibitionsSectionTitle")}
      />
      <AF.RichText
        name={`${language}.exhibitionsContent`}
        label={t("cms.artist.form.exhibitionsContent")}
      />
    </AF.Col>
  );
};

export const BibliographyForm = ({ language }: ArtistFormProps) => {
  const { t } = useTranslation();

  return (
    <AF.Col>
      <AF.Text
        name={`${language}.bibliographySectionTitle`}
        label={t("cms.artist.form.bibliographySectionTitle")}
      />
      <AF.RichText
        name={`${language}.bibliographyContent`}
        label={t("cms.artist.form.bibliographyContent")}
      />
    </AF.Col>
  );
};
